import { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Mail, Send, Eye, EyeOff } from 'lucide-react';

export function SettingsEmailTab({ settings, update, emailStatus, loadEmailStatus, save }) {
  const [sendingTest, setSendingTest] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
      <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Configuration email</h3>

      {emailStatus && (
        <div className={`rounded-xl p-4 flex items-start gap-3 ${emailStatus.configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-300'}`} data-testid="email-status-banner">
          <Mail className={`mt-0.5 shrink-0 ${emailStatus.configured ? 'text-green-600' : 'text-amber-600'}`} size={18} />
          <div>
            <p className={`text-sm font-medium ${emailStatus.configured ? 'text-green-800' : 'text-amber-800'}`}>
              Statut : {emailStatus.configured ? `ACTIF — ${emailStatus.provider_label}` : 'MOCKED (test uniquement)'}
              {emailStatus.detail && <span className="font-normal text-xs ml-2 opacity-70">({emailStatus.detail})</span>}
            </p>
            <p className={`text-xs mt-1 ${emailStatus.configured ? 'text-green-700' : 'text-amber-700'}`}>
              {emailStatus.configured
                ? `Les emails sont envoyes en temps reel${emailStatus.from_email ? ` depuis ${emailStatus.from_email}` : ''}.`
                : 'Les emails ne sont pas reellement envoyes. Configurez Resend ou SMTP ci-dessous pour activer l\'envoi reel.'}
            </p>
          </div>
        </div>
      )}

      <p className="text-sm text-charcoal/70">
        Si aucun fournisseur n'est configuré, les emails restent en mode test (MOCKED) et ne sont pas envoyés.
        Pour activer l'envoi réel, renseignez soit une clé API Resend, soit les paramètres SMTP fournis par votre hébergeur.
        <br /><span className="text-xs text-charcoal/50">Priorité : Resend &gt; SMTP &gt; MOCKED</span>
      </p>

      <div>
        <Label className="text-sm">Email de réception des réservations</Label>
        <Input value={settings.reservationEmailRecipient || ''} onChange={e => update('reservationEmailRecipient', e.target.value)} className="border-garden-200 rounded-xl" placeholder="contact@restaurant.fr" data-testid="email-recipient-input" />
        <p className="text-xs text-charcoal/50 mt-1">Les demandes de réservation seront envoyées à cette adresse</p>
      </div>
      
      <div className="border-t border-garden-100 pt-4">
        <Label className="text-sm">Méthode d'envoi</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="email_provider" value="smtp" checked={settings.email_provider !== 'resend'} onChange={() => update('email_provider', 'smtp')} className="accent-garden-600" />
            <span className="text-sm">SMTP</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="email_provider" value="resend" checked={settings.email_provider === 'resend'} onChange={() => update('email_provider', 'resend')} className="accent-garden-600" />
            <span className="text-sm">Resend</span>
          </label>
        </div>
      </div>
      
      {settings.email_provider !== 'resend' ? (
        <div className="space-y-4 border border-garden-200 rounded-xl p-4 bg-garden-50/50">
          <h4 className="font-medium text-garden-800">Configuration SMTP</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Serveur SMTP</Label>
              <Input value={settings.smtp_host || ''} onChange={e => update('smtp_host', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input type="number" value={settings.smtp_port || 587} onChange={e => update('smtp_port', parseInt(e.target.value))} className="border-garden-200 rounded-xl bg-white" data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Utilisateur</Label>
              <Input value={settings.smtp_user || ''} onChange={e => update('smtp_user', e.target.value)} className="border-garden-200 rounded-xl bg-white" data-testid="smtp-user" />
            </div>
            <div>
              <Label className="text-sm">Mot de passe</Label>
              <Input type="password" value={settings.smtp_password || ''} onChange={e => update('smtp_password', e.target.value)} className="border-garden-200 rounded-xl bg-white" data-testid="smtp-password" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={settings.smtp_secure === 'true'} onCheckedChange={v => update('smtp_secure', v ? 'true' : 'false')} data-testid="smtp-secure" />
            <Label className="text-sm">Connexion sécurisée (TLS/SSL)</Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Email d'expédition</Label>
              <Input value={settings.smtp_from_email || ''} onChange={e => update('smtp_from_email', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="noreply@restaurant.fr" data-testid="smtp-from-email" />
            </div>
            <div>
              <Label className="text-sm">Nom d'expédition</Label>
              <Input value={settings.smtp_from_name || ''} onChange={e => update('smtp_from_name', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="Côté Terrasse" data-testid="smtp-from-name" />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 border border-garden-200 rounded-xl p-4 bg-garden-50/50">
          <h4 className="font-medium text-garden-800">Configuration Resend</h4>
          <div>
            <Label className="text-sm">Clé API Resend</Label>
            <Input type="password" value={settings.resend_api_key || ''} onChange={e => update('resend_api_key', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="re_..." data-testid="resend-api-key" />
            <p className="text-xs text-charcoal/50 mt-1">Obtenez votre clé sur <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-garden-600 underline">resend.com</a></p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Email d'expédition</Label>
              <Input value={settings.smtp_from_email || ''} onChange={e => update('smtp_from_email', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="noreply@restaurant.fr" data-testid="resend-from-email" />
            </div>
            <div>
              <Label className="text-sm">Nom d'expédition</Label>
              <Input value={settings.smtp_from_name || ''} onChange={e => update('smtp_from_name', e.target.value)} className="border-garden-200 rounded-xl bg-white" placeholder="Côté Terrasse" data-testid="resend-from-name" />
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-garden-100 pt-4">
        <Button
          onClick={async () => {
            setSendingTest(true);
            try {
              await save();
              const res = await adminApi.sendTestEmail();
              if (res.data.success) { toast.success(res.data.message); }
              else { toast.error(res.data.message, { duration: 8000 }); }
              loadEmailStatus();
            } catch { toast.error('Erreur de communication avec le serveur'); }
            setSendingTest(false);
          }}
          disabled={sendingTest}
          data-testid="send-test-email-btn"
          className="bg-garden-800 hover:bg-garden-900 text-white rounded-full"
        >
          <Send size={14} className="mr-1.5" />
          {sendingTest ? 'Envoi en cours...' : 'Sauvegarder et envoyer un email test'}
        </Button>
        <p className="text-xs text-charcoal/50 mt-2">Sauvegarde les parametres puis envoie un email test a l'adresse de reception configuree.</p>
      </div>

      <div className="border-t border-garden-100 pt-4 space-y-3">
        <h4 className="text-sm font-medium text-garden-800">Guide rapide par hebergeur</h4>
        <div className="text-xs text-charcoal/60 space-y-2 bg-garden-50/50 rounded-xl p-4">
          <p className="bg-garden-100/60 rounded-lg p-2 border border-garden-200">
            <strong className="text-garden-800">Gandi Simple Hosting (recommande) :</strong> Host: <code className="bg-white px-1 rounded">mail.gandi.net</code> | Port: <code className="bg-white px-1 rounded">587</code> (STARTTLS) ou <code className="bg-white px-1 rounded">465</code> (SSL) | TLS: oui | User: votre adresse email Gandi | Password: mot de passe de la boite mail Gandi.
          </p>
          <p><strong>Gmail SMTP :</strong> Host: <code className="bg-garden-100 px-1 rounded">smtp.gmail.com</code> | Port: <code className="bg-garden-100 px-1 rounded">587</code> | TLS: oui | Utilisez un <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-garden-600 underline">mot de passe d'application</a>.</p>
          <p><strong>OVH SMTP :</strong> Host: <code className="bg-garden-100 px-1 rounded">ssl0.ovh.net</code> | Port: <code className="bg-garden-100 px-1 rounded">465</code> | TLS/SSL: oui</p>
          <p><strong>Resend :</strong> Creez un compte sur <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-garden-600 underline">resend.com</a>, ajoutez votre domaine, puis copiez la cle API.</p>
        </div>
      </div>
    </div>
  );
}
